import type { AxiosBaseQueryMeta } from "../api/types";
import {
  ConsistencyContext,
  SectionEnvelopeWire,
  SectionErrorResponse,
  SectionResponse,
} from "./types";

const STALE_CONTEXT_CODES = new Set([
  "STALE_ACCOUNT_VERSION",
  "STALE_BUSINESS_DATE",
]);
const CONTEXT_PARAMS = new Set(["accountVersion", "businessDate"]);

const asSectionError = (error: unknown): SectionErrorResponse | null =>
  typeof error === "object" && error !== null
    ? (error as SectionErrorResponse)
    : null;

// Both or neither: the server refuses half a pair, so the type of the result cannot express one.
export const contextParams = (
  context: ConsistencyContext | undefined,
): Partial<ConsistencyContext> =>
  context === undefined
    ? {}
    : {
        accountVersion: context.accountVersion,
        businessDate: context.businessDate,
      };

export const toSectionResponse = <Wire, Result>(
  wire: SectionEnvelopeWire<Wire>,
  toResult: (result: Wire) => Result,
): SectionResponse<Result> => ({
  result: toResult(wire.result),
  accountVersion: wire.accountVersion ?? null,
  businessDate: wire.businessDate ?? null,
  freshness: wire.freshness ?? null,
  accountId: wire.accountId ?? null,
});

export const withRejectionStatus = (
  error: unknown,
  meta: AxiosBaseQueryMeta | undefined,
): unknown => {
  const body = asSectionError(error);
  if (body === null || meta?.status === undefined) {
    return error;
  }
  return { ...body, status: meta.status };
};

export const isStaleContextError = (error: unknown): boolean =>
  STALE_CONTEXT_CODES.has(asSectionError(error)?.code ?? "");

export const isSectionUnavailable = (error: unknown): boolean =>
  asSectionError(error)?.code === "COMMAND_CENTRE_SECTION_UNAVAILABLE";

export const isRetryableSectionError = (error: unknown): boolean => {
  const body = asSectionError(error);
  return body?.retryScope === "SECTION" && body.retryable === true;
};

export const isNotFoundError = (error: unknown): boolean =>
  asSectionError(error)?.status === 404;

// `forbidNonWhitelisted` naming one of the pair is how a pre-Release-A server refuses it; any other 400 is a real error.
export const isContextPairRejected = (error: unknown): boolean => {
  const errors = asSectionError(error)?.errors;
  if (!Array.isArray(errors)) {
    return false;
  }
  return errors.some(
    (entry) =>
      typeof entry === "object" &&
      entry !== null &&
      CONTEXT_PARAMS.has(entry.property) &&
      entry.constraints?.whitelistValidation !== undefined,
  );
};

// Recovered by the whole load generation (a restart, the not-found state, the unversioned fallback), never by one section.
export const isGenerationError = (error: unknown): boolean =>
  isStaleContextError(error) ||
  isContextPairRejected(error) ||
  isNotFoundError(error);

// Keeps the payload's own version and `generatedAt`: stamping the current ones on it would claim a rebuild that never happened.
export const asRetained = <Result>(
  response: SectionResponse<Result>,
): SectionResponse<Result> => ({
  ...response,
  freshness:
    response.freshness === null
      ? null
      : { ...response.freshness, state: "RETAINED" },
});
